/**
 * Ebenen-Panel — list of every finished measurement.
 *
 * Each row:
 *   - checkbox to toggle visibility
 *   - colour swatch matching the line colour
 *   - label "Linie 1 — 5.50 m" / "Fläche 1 — 22.50 m²"
 *   - "✕" button to delete
 *
 * Panel hides itself when the list is empty so the canvas stays clean
 * for first-time visitors.
 */

import { useEffect, useRef } from 'react'

import type { Measurement } from '../viewer/measurements'
import { colorForUser, initialsFor } from '../viewer/annotations'

interface MeasurementsPanelProps {
  items: Measurement[]
  onToggle: (id: string) => void
  onRemove: (id: string) => void
  onClearAll: () => void
}

export function MeasurementsPanel({
  items,
  onToggle,
  onRemove,
  onClearAll,
}: MeasurementsPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const prevCountRef = useRef(items.length)

  // Auto-scroll to the bottom when an item is appended (count grew).
  // Skip on removals so the user's read position survives a delete.
  useEffect(() => {
    const el = scrollRef.current
    if (el && items.length > prevCountRef.current) {
      el.scrollTop = el.scrollHeight
    }
    prevCountRef.current = items.length
  }, [items.length])

  if (items.length === 0) return null

  return (
    <div
      style={{
        // Positioned by the right-column container in Scene.tsx; the panel
        // is just a flex item here, free to shrink as needed.
        width: '100%',
        flexShrink: 0,
        // Cap own height so a long measurement list doesn't push the
        // Bilder panel off-screen.
        maxHeight: '50vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(20,20,20,0.92)',
        border: '1px solid #333',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
        color: '#eee',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: 13,
        overflow: 'hidden', // so the header keeps its rounded corners
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderBottom: '1px solid #2a2a2a',
          flexShrink: 0, // never collapses regardless of list length
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 12, letterSpacing: 0.5 }}>
          Messungen
        </div>
        <button
          onClick={onClearAll}
          title="Alle löschen"
          style={{
            background: 'transparent',
            border: '1px solid #444',
            color: '#aaa',
            cursor: 'pointer',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 11,
            fontFamily: 'inherit',
          }}
        >
          Alle löschen
        </button>
      </div>
      <div
        ref={scrollRef}
        style={{
          // The wrapper already caps overall height; let the list shrink
          // and scroll inside whatever space it gets.
          flex: '1 1 auto',
          overflowY: 'auto',
        }}
      >
        {items.map((m) => (
          <MeasurementRow
            key={m.id}
            item={m}
            onToggle={() => onToggle(m.id)}
            onRemove={() => onRemove(m.id)}
          />
        ))}
      </div>
    </div>
  )
}

function MeasurementRow({
  item,
  onToggle,
  onRemove,
}: {
  item: Measurement
  onToggle: () => void
  onRemove: () => void
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderBottom: '1px solid #2a2a2a',
        opacity: item.visible ? 1 : 0.45,
      }}
    >
      <input
        type="checkbox"
        checked={item.visible}
        onChange={onToggle}
        style={{ cursor: 'pointer', accentColor: '#ffd400' }}
        aria-label="anzeigen"
      />
      <span
        title="Farbe"
        style={{
          width: 12,
          height: 12,
          borderRadius: 3,
          background: '#' + item.color.toString(16).padStart(6, '0'),
          border: '1px solid rgba(255,255,255,0.2)',
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1, fontVariantNumeric: 'tabular-nums' }}>{item.label}</span>
      <AuthorBadge item={item} />
      <button
        onClick={onRemove}
        title="Löschen"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#888',
          cursor: 'pointer',
          fontSize: 14,
          padding: '0 4px',
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  )
}

function AuthorBadge({ item }: { item: Measurement }) {
  if (item.pending) {
    return (
      <span
        title="Speichern…"
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.18)',
          borderTopColor: 'rgba(255,255,255,0.7)',
          animation: 'rl-spin 0.8s linear infinite',
          flexShrink: 0,
        }}
      />
    )
  }
  const author = item.createdBy
  if (!author) return null
  const bg = colorForUser(author.id)
  const tooltip = `${author.first_name} ${author.last_name}`.trim() || author.email
  return (
    <span
      title={tooltip}
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: '#' + bg.toString(16).padStart(6, '0'),
        color: '#fff',
        fontSize: 9,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        textShadow: '0 0 1px rgba(0,0,0,0.5)',
        userSelect: 'none',
      }}
    >
      {initialsFor(author)}
    </span>
  )
}
